/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmAccounts.ts
 * PURPOSE: List shell CRM accounts from /api/v1/crm/accounts; soft-delete via DELETE
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, @shared/lib/frontend/api/accountApi Account, ./crmEntityMappers
 * PUBLIC_API: useCrmAccounts
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: SAFE
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Account } from '@shared/lib/frontend/api/accountApi';
import { crmDelete, crmList, crmRestoreAccount, type CrmAccountVisibility } from '@shared/lib/frontend/api/crmApi';
import { mapCrmShellAccountToAccount } from './crmEntityMappers';

export type UseCrmAccountsMutate = {
  deleteAccount: (id: string) => Promise<void>;
  restoreAccount: (id: string) => Promise<void>;
};

export type UseCrmAccountsResult = {
  data: Account[];
  isLoading: boolean;
  error: string | null;
  mutate: UseCrmAccountsMutate;
  refetch: () => Promise<void>;
};

export function useCrmAccounts(visibility: CrmAccountVisibility = 'active'): UseCrmAccountsResult {
  const [data, setData] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await crmList<Record<string, unknown>>('accounts', {
        limit: 200,
        visibility,
      });
      const rows = Array.isArray(res.items) ? res.items : [];
      setData(rows.map((r) => mapCrmShellAccountToAccount(r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, [visibility]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const deleteAccount = useCallback(
    async (id: string) => {
      await crmDelete('accounts', id);
      await refetch();
    },
    [refetch]
  );

  const restoreAccount = useCallback(
    async (id: string) => {
      await crmRestoreAccount(id);
      await refetch();
    },
    [refetch]
  );

  const mutate = useMemo(
    () => ({ deleteAccount, restoreAccount }) satisfies UseCrmAccountsMutate,
    [deleteAccount, restoreAccount]
  );

  return { data, isLoading, error, mutate, refetch };
}
